// components/BatchDialog.tsx
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { AsyncSelectFetchParams } from "@/components/form/types";
import { Spinner } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useBatchByIdQuery,
  useCreateProgramBatchMutation,
  useUpdateBatchMutation,
} from "@/hooks/api/batch.queries";
import {
  BatchParamsType,
  BatchType,
  ProgramBatchCreateType,
} from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import {
  departmentsKey,
  useProgramByIdQuery,
  useDepartmentByIdQuery,
} from "@/hooks/api/department.queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";

// ─── Types ────────────────────────────────────────────────────────────────────

type BatchDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: BatchParamsType;
  batchId?: string;
  batchData?: BatchType | null;
  onCancel: () => void;
};

// ─── Schema (shared for create + edit) ────────────────────────────────────────

const batchSchema = z.object({
  batchName: z.string().min(1, "Batch Name is required"),
  department_id: z.string().min(1, "Department is required"),
  program_id: z.string().min(1, "Program is required"),
  start_year: z.preprocess(
    (v) => Number(v),
    z.number().min(2000, "Invalid year").max(2100, "Invalid year")
  ),
  end_year: z.preprocess(
    (v) => Number(v),
    z.number().min(2000, "Invalid year").max(2100, "Invalid year")
  ),
});

type BatchFormType = {
  batchName: string;
  department_id: string;
  program_id: string;
  start_year: number;
  end_year: number;
};

// ─── Component ────────────────────────────────────────────────────────────────

const BatchDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  batchId,
  onCancel,
}: BatchDialogProps) => {
  const createMutation = useCreateProgramBatchMutation(filters);
  const updateMutation = useUpdateBatchMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Fetch existing batch data in edit mode
  const { data: fetchedBatch, isLoading: isFetchingBatch } = useBatchByIdQuery(
    batchId || "",
    mode === "edit" && isOpen && !!batchId
  );

  // Fetch the program of the batch to get the department_id
  const { data: fetchedProgram, isLoading: isFetchingProgram } =
    useProgramByIdQuery(
      fetchedBatch?.degree_program_id || "",
      mode === "edit" && isOpen && !!fetchedBatch?.degree_program_id
    );

  const parsedDeptId =
    fetchedBatch?.department_id ||
    fetchedProgram?.department_id ||
    (fetchedProgram as any)?.dept_id ||
    "";

  const { data: fetchedDepartment } = useDepartmentByIdQuery(
    parsedDeptId,
    mode === "edit" && isOpen && !!parsedDeptId
  );

  const form = useForm<BatchFormType>({
    resolver: zodResolver(batchSchema) as any,
    defaultValues: {
      batchName: "",
      department_id: "",
      program_id: "",
      start_year: new Date().getFullYear(),
      end_year: new Date().getFullYear() + 4,
    },
  });

  const selectedDeptId = form.watch("department_id");

  // Reset program when department changes
  useEffect(() => {
    if (form.getFieldState("department_id").isDirty) {
      form.setValue("program_id", "");
    }
  }, [selectedDeptId, form]);

  // components/BatchDialog.tsx
  // Reset / pre-fill form when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    if (mode === "edit" && fetchedBatch) {
      // Don't reset if we are just waiting for department to load
      if (!parsedDeptId && isFetchingProgram) return;

      form.reset({
        batchName: fetchedBatch.name,
        department_id: parsedDeptId,
        program_id: fetchedBatch.degree_program_id || "",
        start_year: fetchedBatch.start_year,
        end_year: fetchedBatch.end_year,
      });
    } else if (mode === "create") {
      form.reset({
        batchName: "",
        department_id: "",
        program_id: "",
        start_year: new Date().getFullYear(),
        end_year: new Date().getFullYear() + 4,
      });
    }
  }, [mode, isOpen, fetchedBatch, parsedDeptId, isFetchingProgram, form]);

  const fetchDeptProgramOptions = (params: AsyncSelectFetchParams) =>
    departmentService
      .fetchDepartmentDegrees(selectedDeptId)
      .then((programs: any[]) => {
        const search = (params.search || "").toLowerCase();
        const filtered = programs.filter((p: any) =>
          p.name.toLowerCase().includes(search)
        );
        return {
          items: filtered.map((p: any) => ({
            label: p.name,
            value: p.id,
            subtitle: p.code,
          })),
          hasMore: false,
          total: filtered.length,
        };
      });

  const handleSubmit: SubmitHandler<BatchFormType> = (values) => {
    if (mode === "create") {
      const payload: ProgramBatchCreateType = {
        name: values.batchName,
        start_year: values.start_year,
        end_year: values.end_year,
      };
      createMutation.mutate(
        { programId: values.program_id, data: payload },
        { onSuccess: onCancel }
      );
    } else if (mode === "edit" && batchId) {
      updateMutation.mutate(
        {
          id: batchId,
          data: {
            name: values.batchName,
            start_year: values.start_year,
            end_year: values.end_year,
          },
        },
        { onSuccess: onCancel }
      );
    }
  };

  // Show spinner while fetching batch data in edit mode
  const isLoadingEdit = mode === "edit" && isFetchingBatch;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Batch" : "Edit Batch"}
          </DialogTitle>
        </DialogHeader>

        {isLoadingEdit ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit as any)}
              className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
            >
              <InputField<BatchFormType>
                name="batchName"
                label="Batch Name"
                placeholder="e.g. CSE 2021-2025"
                type="text"
                disabled={isPending}
                required
                className={{ itemClass: "md:col-span-2" }}
              />

              <AsyncSingleSelectField<BatchFormType>
                name="department_id"
                label="Department"
                placeholder="Select Department"
                fetchOptions={departmentService.fetchDepartmentOptions}
                queryKey={["departments", "options"]}
                disabled={isPending}
                required
                defaultOptions={
                  mode === "edit" && fetchedDepartment
                    ? [
                        {
                          label: fetchedDepartment.name,
                          value: parsedDeptId,
                        },
                      ]
                    : []
                }
              />

              <AsyncSingleSelectField<BatchFormType>
                name="program_id"
                label="Degree Program"
                placeholder={
                  selectedDeptId
                    ? "Select Degree Program"
                    : "Select a Department first"
                }
                fetchOptions={fetchDeptProgramOptions}
                queryKey={[
                  ...departmentsKey.all,
                  "programs",
                  selectedDeptId || "",
                ]}
                disabled={isPending || !selectedDeptId}
                required
                defaultOptions={
                  mode === "edit" && fetchedProgram
                    ? [
                        {
                          label: fetchedProgram.name,
                          value: fetchedBatch?.degree_program_id || "",
                        },
                      ]
                    : []
                }
              />

              <InputField<BatchFormType>
                name="start_year"
                label="Start Year"
                placeholder="e.g. 2024"
                type="number"
                disabled={isPending}
                required
              />

              <InputField<BatchFormType>
                name="end_year"
                label="End Year"
                placeholder="e.g. 2028"
                type="number"
                disabled={isPending}
                required
              />

              <DialogFooter className="md:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending
                    ? mode === "create"
                      ? "Adding..."
                      : "Saving..."
                    : mode === "create"
                      ? "Create Batch"
                      : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchDialog;
